import React, { useState } from 'react'
import { fmtShortDay } from '../financial/formatting'
import { CATEGORIES, count, dayTotal, daySegments } from './model'

// Blocked per New York day for the last 14 days, today last and in bold.
// `daily` carries every day, so an empty one is a real zero. Each bar stacks
// the five categories in CATEGORIES order (duplicate at the bottom); the
// exact split is in the tooltip, by mouse or keyboard.
export default function DailyChart({ rows = [] }) {
  const [active, setActive] = useState(null)
  const days = rows.map((r) => ({ date: r.date, segments: daySegments(r), total: dayTotal(r) }))
  const max = Math.max(0, ...days.map((d) => d.total))
  const last = days.length - 1
  const h = (v) => (max > 0 ? (Math.max(0, v) / max) * 90 : 0)

  return (
    <section className="card" aria-labelledby="bk-daily-title" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="bk-card-head">
        <h2 id="bk-daily-title" style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Blocked per day, last 14 days</h2>
        <div aria-hidden="true" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-secondary)' }}>
          {CATEGORIES.map((c) => (
            <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: c.color }} />
              {c.label}
            </span>
          ))}
        </div>
      </div>
      {max === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Nothing was blocked in the last 14 days.</p>
      ) : (
        <div>
          <div className="bk-day-bars" role="list" aria-label="Blocked per day">
            {days.map((d, i) => (
              <div
                key={d.date}
                role="listitem"
                tabIndex={0}
                className="bk-day"
                aria-label={`${fmtShortDay(d.date)}: ${count(d.total)} blocked${d.segments.filter((s) => s.value > 0).map((s) => `, ${count(s.value)} ${s.label.toLowerCase()}`).join('')}`}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
                style={{
                  display: 'flex', flexDirection: 'column-reverse', height: '100%', minWidth: 0,
                  cursor: 'default', borderRadius: 4, background: active === i ? 'var(--chart-cursor)' : 'transparent',
                }}
              >
                {d.segments.filter((s) => s.value > 0).map((s, k, shown) => (
                  <div
                    key={s.id}
                    data-testid={`seg-${d.date}-${s.id}`}
                    style={{
                      height: `${h(s.value)}%`, minHeight: 2, background: s.color,
                      borderRadius: k === shown.length - 1 ? '4px 4px 0 0' : 0,
                    }}
                  />
                ))}
              </div>
            ))}
            {active !== null && <DayTooltip day={days[active]} index={active} count={days.length} barPct={h(days[active].total)} />}
          </div>
          <div className="bk-day-labels" aria-hidden="true">
            {days.map((d, i) => (
              <span
                key={d.date}
                data-today={i === last ? 'true' : undefined}
                style={{ fontSize: 10, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', color: i === last ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: i === last ? 700 : 400 }}
              >
                <span className="bk-day-long">{fmtShortDay(d.date)}</span>
                {/* On a phone "Oct 12" does not fit a 14th of the width: the day alone. The tooltip keeps the full date. */}
                <span className="bk-day-short">{Number(String(d.date).slice(8, 10))}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

// Above the hovered bar; anchored to the bar's outer side near the edges so
// it never spills out of the card.
function DayTooltip({ day, index, count: total, barPct }) {
  const placement = index < 2
    ? { left: `${(index / total) * 100}%`, transform: 'none' }
    : index > total - 3
      ? { left: `${((index + 1) / total) * 100}%`, transform: 'translateX(-100%)' }
      : { left: `${((index + 0.5) / total) * 100}%`, transform: 'translateX(-50%)' }
  return (
    <div
      role="tooltip"
      style={{
        position: 'absolute', bottom: `calc(${barPct}% + 8px)`, zIndex: 2, pointerEvents: 'none',
        padding: '6px 9px', borderRadius: 6, whiteSpace: 'nowrap', fontSize: 12,
        background: 'var(--bg-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow)',
        ...placement,
      }}
    >
      <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>{fmtShortDay(day.date)} · {count(day.total)} blocked</div>
      {day.segments.filter((s) => s.value > 0).reverse().map((s) => (
        <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
          <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />
          {s.label}: <strong>{count(s.value)}</strong>
        </div>
      ))}
    </div>
  )
}
